import * as React from 'react';
import Checkbox from '@mui/material/Checkbox';
import Grid from '@mui/material/Grid';
import { Typography, Box, Button, Tabs, Tab } from '@mui/material';
import { connect } from "react-redux";
import { loginUser, signUp } from '../../app/store/actions/authActions';
import { Navigate, useNavigate, useSearchParams } from "react-router-dom";
import { getSafeRedirectPath } from "../../app/utils/authRedirect";
import { MainInput, MainPassword } from '../../app/components/Styles';
import {
  marketingFonts,
  marketingColors,
  marketingTabsSx,
} from '../../app/components/tickitMarketingTheme';
import { MarketingButton, MarketingFormPage } from '../../app/components/marketing';

function a11yProps(index) {
  return {
    id: `simple-tab-${index}`,
    'aria-controls': `simple-tabpanel-${index}`,
  };
}

function UserLoginForm({
  loginUser,
  signUp,
  isLoading,
  user,
  token,
}) {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [error, setError] = React.useState(null);
  const [terms, setTerms] = React.useState(false);
  const [value, setValue] = React.useState(0);

  const postLoginPath = React.useMemo(() => {
    const redirect = getSafeRedirectPath(searchParams.get("redirect"));
    if (redirect) return redirect;
    if (token) return `/${token.split(" ")[0]}`;
    return "/";
  }, [searchParams, token]);

  if (user && token) {
    return <Navigate to={postLoginPath} replace />;
  }

  const handleSignupSubmit = (event) => {
    event.preventDefault();
    const data = new FormData(event.currentTarget);
    if (data.get("password") !== data.get("password-confirm")) {
      setError("Passwords Do Not Match");
      return;
    }
    signUp(
      {
        name: `${data.get("firstName")} ${data.get("lastName")}`,
        email: data.get("email"),
        phoneNumber: data.get("phoneNumber"),
        password: data.get("password"),
      },
      navigate
    );
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    const data = new FormData(event.currentTarget);
    loginUser({
      email: data.get("email"),
      password: data.get("password"),
    });
  };

  return (
    <MarketingFormPage
      maxWidth="sm"
      title="Welcome to Tickit"
      subtitle="Sign in or create your account"
      loading={isLoading}
    >
      <Box sx={{ width: "100%" }}>
        <Box sx={{ borderBottom: 1, borderColor: marketingColors.border, mb: 2 }}>
          <Tabs
            value={value}
            centered
            onChange={(_event, newValue) => setValue(newValue)}
            aria-label="Login or sign up"
            sx={marketingTabsSx}
          >
            <Tab label="Login" sx={{ mx: 3 }} {...a11yProps(0)} />
            <Tab label="Sign up" sx={{ mx: 3 }} {...a11yProps(1)} />
          </Tabs>
        </Box>

        {value === 0 ? (
          <Box
            component="form"
            onSubmit={handleSubmit}
            role="tabpanel"
            id="simple-tabpanel-0"
            aria-labelledby="simple-tab-0"
            sx={{ mt: 1, width: "100%" }}
          >
            {error && (
              <Typography role="alert" aria-live="polite" sx={{ color: "error.main", mb: 2, fontFamily: marketingFonts.body, fontSize: "0.875rem" }}>
                {error}
              </Typography>
            )}
            <MainInput
              margin="normal"
              required
              fullWidth
              id="login-email"
              label="Email"
              name="email"
              type="email"
              autoComplete="email"
              autoFocus
            />
            <MainPassword
              margin="normal"
              required
              fullWidth
              name="password"
              label="Password"
              id="login-password"
              autoComplete="current-password"
            />
            <MarketingButton type="submit" fullWidth sx={{ mt: 3, mb: 2 }}>
              Log in
            </MarketingButton>
            <MarketingButton
              variant="ghost"
              fullWidth
              onClick={() => navigate("/forgot-password")}
              sx={{ mt: 0.5 }}
            >
              Forgot password?
            </MarketingButton>
          </Box>
        ) : (
          <Box
            component="form"
            onSubmit={handleSignupSubmit}
            role="tabpanel"
            id="simple-tabpanel-1"
            aria-labelledby="simple-tab-1"
            sx={{ mt: 1, width: "100%" }}
          >
            {error && (
              <Typography role="alert" aria-live="polite" sx={{ color: "error.main", mb: 2, fontFamily: marketingFonts.body, fontSize: "0.875rem" }}>
                {error}
              </Typography>
            )}
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <MainInput margin="normal" required fullWidth id="firstName" label="First Name" name="firstName" />
              </Grid>
              <Grid item xs={6}>
                <MainInput margin="normal" required fullWidth id="lastName" label="Last Name" name="lastName" />
              </Grid>
            </Grid>
            <MainInput margin="normal" type="email" required fullWidth id="signup-email" label="Email" name="email" />
            <MainInput margin="normal" fullWidth required id="phoneNumber" label="Phone Number" name="phoneNumber" />
            <MainPassword margin="normal" sx={{ my: 1 }} required fullWidth name="password" label="Password" id="signup-password" autoComplete="new-password" />
            <MainPassword margin="normal" sx={{ my: 1 }} required fullWidth name="password-confirm" label="Confirm Password" id="password-confirm" error={error} />
            <Box sx={{ display: "flex", alignItems: "flex-start", mb: 3 }}>
              <Checkbox
                checked={terms}
                onChange={() => setTerms(!terms)}
                sx={{
                  color: marketingColors.gray500,
                  mt: -0.5,
                  "&.Mui-checked": { color: marketingColors.p700 },
                }}
              />
              <Typography variant="body2" sx={{ color: marketingColors.gray500, fontFamily: marketingFonts.body, lineHeight: 1.5 }}>
                I agree to the{" "}
                <Button
                  type="button"
                  onClick={() => navigate("/terms")}
                  sx={{
                    color: marketingColors.p700,
                    fontWeight: 700,
                    textTransform: "none",
                    minWidth: "auto",
                    p: 0,
                    fontSize: "inherit",
                    verticalAlign: "baseline",
                    "&:hover": { backgroundColor: "transparent", textDecoration: "underline" },
                  }}
                >
                  Terms & Conditions
                </Button>
              </Typography>
            </Box>
            <MarketingButton type="submit" fullWidth disabled={!terms} sx={{ mt: 2, mb: 2 }}>
              Create account
            </MarketingButton>
          </Box>
        )}
      </Box>
    </MarketingFormPage>
  );
}

const mapStateToProps = (state) => ({
  user: state?.auth?.user,
  isLoading: state?.auth?.isLoading,
  token: state?.auth?.token,
});

const mapDispatchToProps = { loginUser, signUp };

export default connect(mapStateToProps, mapDispatchToProps)(UserLoginForm);
